import { useCallback, useEffect, useRef, useState } from 'react'
import { apiClient } from '../../../lib/api/client'
import { useRefetchOnWindowFocus } from '../../../lib/useRefetchOnWindowFocus'
import {
  type S3PutTicket,
  UploadTooLargeError,
  assertWithinMaxBytes,
  putFileToS3,
} from '../../uploads/s3Upload'
import { generateAndUploadThumbnail } from '../../uploads/thumbnail/generateThumbnail'
import { problemTitle } from '../usePartsLibrary'
import { TECHNICAL_DRAWING_ACCEPT, TECHNICAL_DRAWING_CONTENT_TYPE } from './types'
import type {
  PartAttachment,
  PartAttachmentFileKind,
  PartAttachmentPresignTicket,
  PendingAttachment,
} from './types'

/**
 * The data-layer seam for the durable Part Attachments UI (UID-155/B3),
 * wired to the REAL B2 BFF (UID-154) end to end — presign→PUT→persist→list→
 * download, no mock service. `apiClient` is the ONLY client this vertical
 * calls through (matches every other BFF-backed seam in this codebase, e.g.
 * `defaultPartsLibraryService`, `defaultUploadsService`).
 */
export interface PartAttachmentsService {
  listAttachments(partId: string): Promise<readonly PartAttachment[]>
  presignUpload(
    partId: string,
    request: {
      readonly fileKind: PartAttachmentFileKind
      readonly filename: string
      readonly contentType: string
      readonly sizeBytes: number
    },
  ): Promise<PartAttachmentPresignTicket>
  createAttachment(partId: string, uploadId: string): Promise<PartAttachment>
  /** Resolves a short-lived presigned GET URL for the attachment's bytes
   * (AC4/download) — the server-only object key never transits this call;
   * the browser only ever sees the presigned `url`. */
  getDownloadUrl(partId: string, attachmentId: string): Promise<string>
  /**
   * UID-335 — mint a presigned PUT for a client-rendered thumbnail of the
   * attachment `uploadId` just persisted on `partId`. Id-addressed by the
   * PART's own id server-side (not `uploadId`) — see the BFF route's doc
   * comment; this call site never needs to know the derived key.
   */
  presignThumbnail(partId: string, uploadId: string, sizeBytes: number): Promise<S3PutTicket>
}

export const defaultPartAttachmentsService: PartAttachmentsService = {
  async listAttachments(partId) {
    const { data, error } = await apiClient.GET('/parts/{partId}/attachments', {
      params: { path: { partId } },
    })
    if (error || !data) {
      throw new Error(problemTitle(error, 'We couldn’t load this part’s attachments.'))
    }
    return data.attachments
  },
  async presignUpload(partId, request) {
    const { data, error } = await apiClient.POST('/parts/{partId}/attachments/presign', {
      params: { path: { partId } },
      body: request,
    })
    if (error || !data) {
      throw new Error(problemTitle(error, 'Upload could not be started'))
    }
    return data
  },
  async createAttachment(partId, uploadId) {
    const { data, error } = await apiClient.POST('/parts/{partId}/attachments', {
      params: { path: { partId } },
      body: { uploadId },
    })
    if (error || !data) {
      throw new Error(problemTitle(error, 'We couldn’t save this attachment.'))
    }
    return data
  },
  async getDownloadUrl(partId, attachmentId) {
    const { data, error } = await apiClient.GET(
      '/parts/{partId}/attachments/{attachmentId}/download',
      {
        params: { path: { partId, attachmentId } },
      },
    )
    if (error || !data) {
      throw new Error(problemTitle(error, 'We couldn’t prepare this download.'))
    }
    return data.url
  },
  async presignThumbnail(partId, uploadId, sizeBytes) {
    const { data, error } = await apiClient.POST(
      '/parts/{partId}/attachments/{uploadId}/thumbnail/presign',
      {
        params: { path: { partId, uploadId } },
        body: { sizeBytes },
      },
    )
    if (error || !data) {
      throw new Error(problemTitle(error, 'Thumbnail could not be started'))
    }
    return data
  },
}

export type PartAttachmentsState =
  | { readonly status: 'loading' }
  | { readonly status: 'error'; readonly message: string }
  | { readonly status: 'ready'; readonly attachments: readonly PartAttachment[] }

function makeLocalId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export interface UsePartAttachmentsResult {
  readonly state: PartAttachmentsState
  /** Local, not-yet-persisted selection(s) mid-flight — AC3's "clear/remove
   * affordance" targets these, never a persisted `PartAttachment` (there is
   * no delete-attachment endpoint in B0). */
  readonly pending: readonly PendingAttachment[]
  /** A WCAG 3.3.1-compliant, text-carried validation/server error tied to
   * the control (not a toast, not color-only) — cleared on the next
   * attempt. */
  readonly formError: string | null
  /** The latest human-readable status for the `aria-live` region (AC5) —
   * e.g. "Uploading drawing.pdf…", "drawing.pdf attached.". */
  readonly announcement: string | null
  readonly refetch: () => void
  readonly uploadFile: (file: File, fileKind: PartAttachmentFileKind) => Promise<void>
  /** Clears a still-pending (not yet persisted) local selection — see
   * `pending`'s doc comment. No-op / never called for a persisted row. */
  readonly clearPending: (localId: string) => void
  /** Resolves the presigned GET URL for a persisted attachment and opens it
   * in a new tab (AC4). The server-only object key never reaches this
   * layer — only the short-lived presigned `url` does. Surfaces a failure
   * as `formError` text rather than a silent no-op. */
  readonly downloadAttachment: (attachmentId: string) => Promise<void>
}

/**
 * Drives the "Technical drawing (optional)" control (and any other
 * `fileKind` a future embed passes) through the full B0 keyless flow:
 * client validation → presign → PUT to S3 → persist → refetch the list.
 * Every step's failure surfaces as `formError` TEXT tied to the control
 * (WCAG 3.3.1, AC2) — never color-only, never a silent toast.
 */
export function usePartAttachments(
  partId: string,
  service: PartAttachmentsService = defaultPartAttachmentsService,
): UsePartAttachmentsResult {
  const [state, setState] = useState<PartAttachmentsState>({ status: 'loading' })
  const [generation, setGeneration] = useState(0)
  const [pending, setPending] = useState<readonly PendingAttachment[]>([])
  const [formError, setFormError] = useState<string | null>(null)
  const [announcement, setAnnouncement] = useState<string | null>(null)
  // UID-335 — distinguishes a genuine navigation to a DIFFERENT part (must
  // show loading) from a same-part background refetch (`refetch()`'s
  // generation bump, incl. the automatic focus one below): only the former
  // resets `state` to `loading`. A silent background refetch keeps showing
  // the current attachments until the new fetch resolves, so a background
  // thumbnail catching up never flashes the section back to its skeleton.
  const prevPartIdRef = useRef<string | null>(null)

  // `generation` is a manual re-fetch trigger (bumped by `refetch`/after a
  // successful upload), not read in the effect body.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above.
  useEffect(() => {
    let cancelled = false
    const partChanged = prevPartIdRef.current !== partId
    prevPartIdRef.current = partId
    if (partChanged) {
      setState({ status: 'loading' })
    }
    service
      .listAttachments(partId)
      .then((attachments) => {
        if (!cancelled) setState({ status: 'ready', attachments })
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          // A background refetch that fails keeps showing the last good data
          // rather than clobbering it with an error the user never asked for.
          setState((prev) =>
            !partChanged && prev.status === 'ready'
              ? prev
              : {
                  status: 'error',
                  message:
                    error instanceof Error
                      ? error.message
                      : 'We couldn’t load this part’s attachments.',
                },
          )
        }
      })
    return () => {
      cancelled = true
    }
  }, [partId, service, generation])

  const refetch = useCallback(() => setGeneration((g) => g + 1), [])

  // UID-335 — a thumbnail generated for a "model" attachment while this tab
  // was in the background shows up without a manual reload: refetch
  // whenever the tab regains visibility.
  useRefetchOnWindowFocus(refetch)

  // `setPending` (from `useState`) is referentially stable across renders,
  // so these two helpers are too — they're plain functions (not wrapped in
  // `useCallback`) purely to avoid an unnecessary dependency-array entry on
  // every callback below that uses them.
  const updatePending = useCallback((localId: string, patch: Partial<PendingAttachment>) => {
    setPending((prev) => prev.map((p) => (p.localId === localId ? { ...p, ...patch } : p)))
  }, [])

  const removePending = useCallback((localId: string) => {
    setPending((prev) => prev.filter((p) => p.localId !== localId))
  }, [])

  const clearPending = useCallback(
    (localId: string) => {
      removePending(localId)
    },
    [removePending],
  )

  const uploadFile = useCallback(
    async (file: File, fileKind: PartAttachmentFileKind) => {
      setFormError(null)

      // Client-side type check BEFORE presign (AC2) — today the only
      // reusable control is "Technical drawing," which is `.pdf`-only.
      // `contentType` can be spoofed/empty in some browsers, so we also
      // check the extension, matching `Dropzone`'s allowlist precedent.
      if (fileKind === 'technical_drawing') {
        const looksLikePdf =
          file.type === TECHNICAL_DRAWING_CONTENT_TYPE ||
          file.name.toLowerCase().endsWith(TECHNICAL_DRAWING_ACCEPT)
        if (!looksLikePdf) {
          setFormError(`Unsupported file type. Supported file format: ${TECHNICAL_DRAWING_ACCEPT}`)
          return
        }
      }

      const localId = makeLocalId()
      setPending((prev) => [
        ...prev,
        { localId, filename: file.name, sizeBytes: file.size, status: 'uploading', progress: 0 },
      ])
      setAnnouncement(`Uploading ${file.name}…`)

      try {
        const ticket = await service.presignUpload(partId, {
          fileKind,
          filename: file.name,
          contentType: file.type || TECHNICAL_DRAWING_CONTENT_TYPE,
          sizeBytes: file.size,
        })

        // Client size guard BEFORE the PUT (AC2) — mirrors
        // `useUploadFlow`'s existing pattern; the server still enforces its
        // own max independently (413).
        assertWithinMaxBytes(file, ticket)

        await putFileToS3(file, ticket, {
          onProgress: (progress) => updatePending(localId, { progress }),
        })

        updatePending(localId, { status: 'persisting', progress: 100 })
        await service.createAttachment(partId, ticket.uploadId)

        removePending(localId)
        setAnnouncement(`${file.name} attached.`)
        refetch()

        // UID-335 — fire thumbnail generation in the BACKGROUND (not awaited,
        // same calling convention as `useUploadFlow`), keyed to the PART's
        // own id so it displays everywhere the part's thumbnail already
        // resolves from (parts library/RFQ/orders). Only `model` (the CAD
        // geometry kind) can render anything — a technical_drawing/
        // specification/other attachment's `kindForFile` classifier returns
        // null regardless, but gating here skips the wasted `arrayBuffer()`
        // read. Best-effort: a failure never affects the attachment upload,
        // already persisted above.
        if (fileKind === 'model') {
          void generateAndUploadThumbnail(file, (thumbSizeBytes) =>
            service.presignThumbnail(partId, ticket.uploadId, thumbSizeBytes),
          )
        }
      } catch (error) {
        const message =
          error instanceof UploadTooLargeError
            ? error.message
            : error instanceof Error
              ? error.message
              : 'Upload failed. Try again.'
        updatePending(localId, { status: 'error', error: message })
        setFormError(message)
        setAnnouncement(`${file.name} failed to upload: ${message}`)
      }
    },
    [partId, service, refetch, updatePending, removePending],
  )

  const downloadAttachment = useCallback(
    async (attachmentId: string) => {
      try {
        const url = await service.getDownloadUrl(partId, attachmentId)
        window.open(url, '_blank', 'noopener,noreferrer')
      } catch (error) {
        setFormError(error instanceof Error ? error.message : 'We couldn’t prepare this download.')
      }
    },
    [partId, service],
  )

  return {
    state,
    pending,
    formError,
    announcement,
    refetch,
    uploadFile,
    clearPending,
    downloadAttachment,
  }
}
